import { useCallback, useEffect, useState } from 'react';
import { consent, preferenceStorage } from '../../consent/consent.js';
import { offerIsLive } from './offer.js';
import { useRenderNow } from '../../lib/hydration.js';

/**
 * When the welcome card is allowed to appear, and when it stops.
 *
 * Three rules, and they are all about not ambushing anybody. It never opens on
 * load, because a full-screen offer over a page nobody has read yet is the
 * thing people close without looking. It opens once the visitor has shown they
 * are here, either by staying a few seconds or by scrolling at all, whichever
 * happens first. And it opens once per visitor, ever: the flag is written the
 * moment it opens, not when it is dismissed, so a card that was closed by
 * reloading the page does not come back.
 *
 * Past the offer's end date none of this runs, the card cannot open and the
 * pill that reopens it does not render, which is what keeps an expired offer
 * from living on in a corner of the page.
 */

const SEEN_KEY = 'krevya.landing.offerSeen';
const OPEN_AFTER_MS = 4200;

function alreadySeen(): boolean {
  try {
    return preferenceStorage().getItem(SEEN_KEY) === '1';
  } catch {
    // Storage blocked: treat it as seen rather than show the card on every
    // single page view to someone who cannot be remembered.
    return true;
  }
}

function markSeen(): void {
  try {
    preferenceStorage().setItem(SEEN_KEY, '1');
  } catch {
    // Nothing to do, and nothing worth telling the visitor about.
  }
}

export function useWelcomeOffer() {
  const live = offerIsLive(useRenderNow());
  const [open, setOpen] = useState(false);
  /**
   * Whether the pill that reopens the card should be shown at all.
   *
   * Starts as the answer rather than false: the landing page now paints from
   * prerendered HTML, and a pill arriving one effect later pushed the hero
   * down under the reader.
   */
  const [offered, setOffered] = useState(live);

  useEffect(() => {
    if (!live) return;
    setOffered(true);
    /*
     * The card waits for the cookie question to be answered. Two things asking
     * for a decision at once is the ambush this hook exists to avoid, and the
     * answer also decides whether "seen" can be remembered past this tab.
     */
    let timer = 0;
    const show = () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', show);
      markSeen();
      setOpen(true);
    };
    const stopWaiting = consent.whenAnswered(() => {
      if (alreadySeen()) return;
      timer = window.setTimeout(show, OPEN_AFTER_MS);
      window.addEventListener('scroll', show, { passive: true, once: true });
    });
    return () => {
      stopWaiting();
      window.clearTimeout(timer);
      window.removeEventListener('scroll', show);
    };
  }, [live]);

  const reopen = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  return { live, open, offered: offered && live, reopen, close };
}
